import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { useEffect, useRef, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { Toaster } from "@/components/ui/sonner";
import { ChatWidget } from "@/components/chat/ChatWidget";
import { ConsentBanner } from "@/components/ConsentBanner";
import { BookingDialogHost } from "@/components/landing/BookingDialogHost";
import { COMPANY, SOCIAL } from "@/lib/company";
import { CONSENT_COOKIE, resolveConsent, type ConsentMode } from "@/lib/consent";
import { META_PIXEL_ID, PIXEL_BOOTSTRAP, trackPixel } from "@/lib/meta-pixel";
import { MOTION_BOOTSTRAP } from "@/lib/motion";
import { OFFERS } from "@/lib/pricing";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

// Props come from the router rather than being restated here: `error` is
// typed `unknown`, not `Error`, and a hand-written copy of the shape silently
// stops matching when the library tightens it.
function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

/**
 * Decided on the server, per request, from the country Cloudflare stamps on
 * it and the visitor's own earlier answer. It has to be decided before the
 * HTML is written: for a visitor in a consent region the pixel must not be in
 * <head> at all, and the client cannot take a script back out once the
 * browser has run it.
 */
const getConsentMode = createServerFn({ method: "GET" }).handler(async (): Promise<ConsentMode> => {
  const { getCookie, getRequestHeader } = await import("@tanstack/react-start/server");
  return resolveConsent(getRequestHeader("cf-ipcountry"), getCookie(CONSENT_COOKIE));
});

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  loader: async () => {
    try {
      return { consent: await getConsentMode() };
    } catch (error) {
      // Measurement is the only thing at stake here; the page is not. "ask"
      // fires nothing, and the log line keeps the failure from being silent.
      console.error("[consent] could not resolve the consent mode:", error);
      return { consent: "ask" as ConsentMode };
    }
  },
  // The country does not change between client-side navigations, and a fresh
  // server round-trip on every route would be paid for nothing.
  staleTime: Infinity,
  shouldReload: false,

  head: ({ loaderData }) => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Adkiro — Meta Ads for Property" },
      {
        name: "description",
        content:
          "Founder-led Facebook and Instagram advertising for property agencies, brokers and developers in Dubai, Europe and the US.",
      },
      {
        name: "robots",
        content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      { name: "author", content: "Adkiro" },
      // Proves domain ownership to Meta, which gates iOS attribution and
      // event-priority control. Must be server-rendered: Meta's crawler does
      // not run JavaScript.
      {
        name: "facebook-domain-verification",
        content: "9wi3023b0djpzciqzd7cjch5wuuduo",
      },
      { property: "og:title", content: "Adkiro — Meta Ads for Property" },
      {
        property: "og:description",
        content:
          "Founder-led Facebook and Instagram advertising for property agencies, brokers and developers in Dubai, Europe and the US.",
      },
      { property: "og:type", content: "website" },
      // twitter:card is summary_large_image, which renders nothing without an
      // image. Every service page sets one; the site-wide default was missing,
      // so sharing the homepage produced a preview with no picture.
      { property: "og:image", content: "https://adkiro.com/logo.png?v=20260907" },
      { property: "og:image:alt", content: "Adkiro" },
      { name: "twitter:image", content: "https://adkiro.com/logo.png?v=20260907" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      // Google's favicon crawler looks for /favicon.ico at the site root first,
      // and wants a square that is a multiple of 48px — the previous icon was
      // 64x64 with no .ico at all, so Google fell back to a generated letter.
      { rel: "icon", href: "/favicon.ico?v=20260907", sizes: "any" },
      { rel: "icon", href: "/favicon.png?v=20260907", type: "image/png", sizes: "192x192" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png?v=20260907" },
    ],
    scripts: [
      // First, so the page is laid out for motion or for stillness before
      // anything is painted.
      { children: MOTION_BOOTSTRAP },
      // Only when this request may carry the pixel — see getConsentMode.
      ...(loaderData?.consent === "allowed" ? [{ children: PIXEL_BOOTSTRAP }] : []),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://adkiro.com/#organization",
              name: COMPANY.brand,
              legalName: COMPANY.legalName,
              alternateName: COMPANY.legalNameLocal,
              vatID: COMPANY.vat,
              identifier: {
                "@type": "PropertyValue",
                propertyID: "UIC",
                value: COMPANY.uic,
              },
              url: "https://adkiro.com/",
              logo: {
                "@type": "ImageObject",
                url: "https://adkiro.com/logo.png?v=20260907",
                contentUrl: "https://adkiro.com/logo.png?v=20260907",
                width: 512,
                height: 512,
                caption: "Adkiro",
              },
              image: "https://adkiro.com/logo.png?v=20260907",
              description:
                "Founder-led Meta Ads for property agencies, brokers and developers in Dubai, Europe and the United States.",
              knowsAbout: [
                "Meta Ads management",
                "Facebook advertising",
                "Instagram advertising",
                "Real estate advertising",
                "Property lead generation",
                "Meta Pixel",
                "Conversions API",
              ],
              // sameAs is how Google connects this site to the accounts that
              // represent it. Without it the profiles and the domain are
              // unrelated entities, which is the state this site was in.
              // Facebook uses the numeric profile.php URL because the Page has
              // no username yet; swap it for the vanity URL once one is set.
              sameAs: [SOCIAL.instagram, SOCIAL.youtube, SOCIAL.facebook, SOCIAL.linkedin],
              founder: {
                "@type": "Person",
                "@id": "https://adkiro.com/about#kristian-kinchev",
                name: COMPANY.managingDirector,
                url: "https://adkiro.com/about",
                // His own LinkedIn profile belongs to the Person entity; the
                // company Page is in the Organization's sameAs above.
                sameAs: [SOCIAL.founderLinkedin],
              },
              address: {
                "@type": "PostalAddress",
                streetAddress: COMPANY.address.street,
                postalCode: COMPANY.address.postalCode,
                addressLocality: COMPANY.address.city,
                addressCountry: COMPANY.address.countryCode,
              },
              email: COMPANY.email,
              telephone: COMPANY.phoneE164,
              contactPoint: [
                {
                  "@type": "ContactPoint",
                  contactType: "sales",
                  email: COMPANY.email,
                  telephone: COMPANY.phoneE164,
                  availableLanguage: ["English"],
                },
              ],
              areaServed: [
                { "@type": "Place", name: "Europe" },
                { "@type": "Country", name: "United States" },
                { "@type": "Country", name: "United Arab Emirates" },
              ],
              hasOfferCatalog: {
                "@type": "OfferCatalog",
                name: "Meta advertising for property",
                itemListElement: OFFERS.map((offer) => ({
                  "@type": "Offer",
                  ...(offer.schemaPrice !== undefined && {
                    price: offer.schemaPrice,
                    priceCurrency: "USD",
                  }),
                  itemOffered: {
                    "@type": "Service",
                    name: offer.name,
                    url: `https://adkiro.com/meta-ads-pricing#${offer.id}`,
                  },
                })),
              },
            },
            {
              "@type": "WebSite",
              "@id": "https://adkiro.com/#website",
              url: "https://adkiro.com/",
              name: "Adkiro",
              description: "Founder-led Meta Ads for property agencies, brokers and developers.",
              inLanguage: "en",
              publisher: { "@id": "https://adkiro.com/#organization" },
            },
          ],
        }),
      },
    ],
  }),

  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    // The script above adds a class to <html> before React hydrates it.
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const { consent } = Route.useLoaderData();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isInitialPath = useRef(true);

  // Client-side navigation doesn't re-run the head snippet, so every route
  // after the first would otherwise go unrecorded. A no-op while the pixel
  // is not loaded: trackPixel checks for fbq before calling it.
  useEffect(() => {
    if (isInitialPath.current) {
      isInitialPath.current = false;
      return;
    }
    trackPixel("PageView");
  }, [pathname]);

  return (
    <QueryClientProvider client={queryClient}>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
      {consent === "allowed" && (
        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            alt=""
            src={`https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`}
          />
        </noscript>
      )}
      {consent === "ask" && <ConsentBanner />}
      <BookingDialogHost />
      <Toaster position="top-center" />
      <ChatWidget />
    </QueryClientProvider>
  );
}
